import { Grid, InputAdornment, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { FormikDecimalTextField } from "@shared/formik/FormikDecimalTextField";
import { FormikProps } from "formik";

import { IOptionItem } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocomplete.types";
import { FormikAutocompleteSelect } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocompleteSelect";
import { useTranslation } from "@/translations";

import { AppointmentTargetCalculatorFormValues } from "../AppointmentTargetCalculatorDialog.types";
import {
  FULL_TIME_DAYS_PER_WEEK,
  TRANSLATION_BASE,
  usesManualCalendarWorkdays,
} from "../AppointmentTargetCalculatorDialog.utils";

interface AppointmentTargetInputsProps {
  formik: FormikProps<AppointmentTargetCalculatorFormValues>;
  monthOptions: IOptionItem[];
}

// A unit pinned at the right edge of a number field (e.g. "Std. / Woche", "Tage").
const unitAdornment = (unit: string) => ({
  input: {
    endAdornment: (
      <InputAdornment position="end">
        <Typography variant="caption" color="text.secondary">
          {unit}
        </Typography>
      </InputAdornment>
    ),
  },
});

export function AppointmentTargetInputs({ formik, monthOptions }: AppointmentTargetInputsProps) {
  const { t } = useTranslation();

  const usesCalendar = formik.values.workdays === FULL_TIME_DAYS_PER_WEEK;
  const usesManualWorkdays = usesManualCalendarWorkdays(formik.values.month);

  return (
    <Stack flex={1} gap={3} py={2}>
      <Typography variant="overline" color="text.secondary">
        {t(`${TRANSLATION_BASE}.inputsTitle`)}
      </Typography>

      <Grid container rowSpacing={2} columnSpacing={2}>
        <Grid size={12}>
          <TextField
            select
            fullWidth
            size="small"
            label={t(`${TRANSLATION_BASE}.fields.workModel`)}
            value={formik.values.workdays}
            onChange={(event) => {
              void formik.setFieldValue("workdays", Number(event.target.value));
            }}
            helperText=" "
            slotProps={{ inputLabel: { sx: { fontSize: "0.875rem" } } }}
          >
            <MenuItem value={5}>{t(`${TRANSLATION_BASE}.workModels.fiveDays`)}</MenuItem>
            <MenuItem value={4}>{t(`${TRANSLATION_BASE}.workModels.fourDays`)}</MenuItem>
            <MenuItem value={3}>{t(`${TRANSLATION_BASE}.workModels.threeDays`)}</MenuItem>
            <MenuItem value={2}>{t(`${TRANSLATION_BASE}.workModels.twoDays`)}</MenuItem>
            <MenuItem value={1}>{t(`${TRANSLATION_BASE}.workModels.oneDay`)}</MenuItem>
          </TextField>
        </Grid>

        <Grid size={12}>
          <FormikDecimalTextField
            formik={formik}
            fieldName="weeklyHours"
            label={t(`${TRANSLATION_BASE}.fields.weeklyHours`)}
            minValue={0}
            required
            slotProps={unitAdornment(t(`${TRANSLATION_BASE}.fields.weeklyHoursUnit`))}
          />
        </Grid>

        {usesCalendar ? (
          <>
            <Grid size={12}>
              <FormikAutocompleteSelect
                formik={formik}
                fieldName="month"
                label={t(`${TRANSLATION_BASE}.fields.month`)}
                options={monthOptions}
              />
            </Grid>
            {usesManualWorkdays && (
              <Grid size={12}>
                <FormikDecimalTextField
                  formik={formik}
                  fieldName="calendarWorkdays"
                  label={t(`${TRANSLATION_BASE}.fields.calendarWorkdays`)}
                  decimalScale={0}
                  minValue={0}
                  required
                  slotProps={unitAdornment(t(`${TRANSLATION_BASE}.fields.calendarWorkdaysUnit`))}
                />
              </Grid>
            )}
            <Grid size={6}>
              <FormikDecimalTextField
                formik={formik}
                fieldName="vacationDays"
                label={t(`${TRANSLATION_BASE}.fields.vacationDays`)}
                decimalScale={0}
                minValue={0}
              />
            </Grid>
            <Grid size={6}>
              <FormikDecimalTextField
                formik={formik}
                fieldName="trainingDays"
                label={t(`${TRANSLATION_BASE}.fields.trainingDays`)}
                decimalScale={0}
                minValue={0}
              />
            </Grid>
            <Grid size={12}>
              <FormikDecimalTextField
                formik={formik}
                fieldName="otherAbsenceDays"
                label={t(`${TRANSLATION_BASE}.fields.otherAbsenceDays`)}
                decimalScale={0}
                minValue={0}
              />
            </Grid>
          </>
        ) : (
          <Grid size={12}>
            <FormikDecimalTextField
              formik={formik}
              fieldName="actualWorkdays"
              label={t(`${TRANSLATION_BASE}.fields.actualWorkdays`)}
              helperText={t(`${TRANSLATION_BASE}.fields.actualWorkdaysHint`)}
              decimalScale={0}
              minValue={0}
              required
            />
          </Grid>
        )}

        <Grid size={6}>
          <FormikDecimalTextField
            formik={formik}
            fieldName="target1bPerWeek"
            label={t(`${TRANSLATION_BASE}.fields.target1bPerWeek`)}
            decimalScale={0}
            minValue={0}
          />
        </Grid>
        <Grid size={6}>
          <FormikDecimalTextField
            formik={formik}
            fieldName="targetEgPerWeek"
            label={t(`${TRANSLATION_BASE}.fields.targetEgPerWeek`)}
            decimalScale={0}
            minValue={0}
          />
        </Grid>
      </Grid>
    </Stack>
  );
}
